import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import React from "react";
import ReactDOM from "react-dom/client";
import { AuthProviders } from "./components/providers";
import {
	lobuDeepLinkScheme,
	stripLobuSchemeMarker,
} from "./lib/lobu-deep-link";
import {
	hidePublicShellAfterPaint,
	isRootReadyForPublicShellHide,
	PUBLIC_SHELL_READY_EVENT,
} from "./lib/public-shell";
import { initTelemetry } from "./lib/telemetry";
import { reencodeLiteralEntityTypePath } from "./lib/url";
import { queryClient, router } from "./router";
import "./index.css";

// Capture the Mac app's build-scoped scheme marker before the root route can
// redirect and drop its search parameters. Native actions rendered after that
// redirect can then keep targeting the Debug app that opened this page.
lobuDeepLinkScheme();
const searchWithoutScheme = stripLobuSchemeMarker(window.location.search);
if (searchWithoutScheme !== window.location.search) {
	window.history.replaceState(
		window.history.state,
		"",
		`${window.location.pathname}${searchWithoutScheme}${window.location.hash}`,
	);
}

// Canonicalize a literal `$`-prefixed entity-type segment (e.g. a pasted or
// bookmarked `/<org>/$member/<slug>`) back to its `%24` form BEFORE the router
// reads the location — otherwise TanStack parses `$member` as a route param and
// the page 404s. Runs on hard load; in-app navigation already encodes.
{
	const canonical = reencodeLiteralEntityTypePath(window.location.pathname);
	if (canonical !== window.location.pathname) {
		window.history.replaceState(
			window.history.state,
			"",
			`${canonical}${window.location.search}${window.location.hash}`,
		);
	}
}

// PAT-in-URL handoff: when a deep link carries `?lobu_token=<PAT>`, swap it
// for a session cookie before the SPA boots. The server endpoint validates
// the PAT, mints a Better Auth session, and 302s back to `next` (the
// original path with the token stripped). Used by the macOS menu-bar app
// and operators sharing pre-authed deep links.
const tokenParam = new URLSearchParams(window.location.search).get(
	"lobu_token",
);
if (tokenParam) {
	const url = new URL(window.location.href);
	url.searchParams.delete("lobu_token");
	const next = url.pathname + url.search + url.hash;
	window.location.replace(
		`/api/exchange-token?token=${encodeURIComponent(tokenParam)}&next=${encodeURIComponent(next)}`,
	);
	// Halt SPA init until the navigation lands so we don't briefly render the
	// login screen against the unauthenticated cookie state.
	await new Promise(() => {});
}

initTelemetry();

const root = document.getElementById("root");
if (!root) {
	throw new Error("Root element not found");
}
const appRoot = root;

ReactDOM.createRoot(appRoot).render(
	<React.StrictMode>
		<QueryClientProvider client={queryClient}>
			<AuthProviders>
				<RouterProvider router={router} />
			</AuthProviders>
		</QueryClientProvider>
	</React.StrictMode>,
);

// The HTML template keeps public content as the no-JS/failed-boot fallback.
// Its loading presentation stays up until the actual public route is ready,
// including auth/org/sidebar queries, so startup never flashes another layout.
const ssrShell = document.getElementById("ssr-hydrate-shell");
if (ssrShell) {
	let hidden = false;
	let observer: MutationObserver | null = null;

	const cleanup = () => {
		window.removeEventListener(PUBLIC_SHELL_READY_EVENT, tryHideShell);
		observer?.disconnect();
	};

	const removeShell = () => {
		ssrShell.remove();
		document.getElementById("public-boot-status")?.remove();
		document.documentElement.classList.remove("public-app-loading");
		cleanup();
	};

	const hideShell = () => {
		if (hidden) return;
		hidden = true;
		hidePublicShellAfterPaint(ssrShell, removeShell);
	};

	function observeUntilReady() {
		if (observer) return;
		observer = new MutationObserver(() => {
			if (isRootReadyForPublicShellHide(appRoot)) {
				hideShell();
			}
		});
		observer.observe(appRoot, {
			attributes: true,
			childList: true,
			characterData: true,
			subtree: true,
		});
	}

	function tryHideShell() {
		if (isRootReadyForPublicShellHide(appRoot)) {
			hideShell();
			return;
		}
		observeUntilReady();
	}

	window.addEventListener(PUBLIC_SHELL_READY_EVENT, tryHideShell);

	// Safety valve: if the ready event was missed but the client has meaningful
	// non-loading content, fade out. If React failed or is still showing a public
	// loading placeholder, preserve the server-rendered HTML instead of blanking.
	window.setTimeout(tryHideShell, 5000);
} else {
	document.getElementById("public-boot-status")?.remove();
	document.documentElement.classList.remove("public-app-loading");
}
